// What a bird IS, as against what it sounds like or what it is called.
//
// Every other thing this app knows about a species is either a name, a
// recording, a photograph or a schedule. None of that says what the bird eats,
// where it lives, how big it is, what its feet are for or what it builds — the
// half of knowing a bird that a field guide spends its prose on and that no
// quiz here has ever asked for.
//
// Five axes, each a closed vocabulary. The shape is `src/core/compare.ts`'s
// `Facet` block scaled up: a display order, a label per value, a single
// lowercase word as the value itself. Two rules hold the whole file together.
//
// EVERY AXIS IS A LIST, `size` included. A robin eats invertebrates and fruit and
// lives in three kinds of country, and a bird eleven inches long straddling two
// buckets is a fact about the bird rather than a gap in the data. One code path
// for all five is worth more than the two bytes a scalar would save, and the
// Birdle board leans on it directly: a slot there is solved by naming ANY value
// the bird holds, so a generalist is the easy puzzle and a specialist the hard
// one, which is the right way round.
//
// THE VOCABULARIES ARE COARSE ON PURPOSE. Thirteen habitats is not a biome
// classification and one `invertebrate` covers a caterpillar and a crab; these
// are the distinctions a birder makes out loud, at the grain where a learner can
// actually answer. A finer axis would be more nearly true and much less
// answerable — see the note on `prey` below for the pair that had to be merged
// for exactly that reason.
//
// Kept free of the DOM, of media and of the region singleton so `test/` can
// reach it — and, more sharply, so `tools/pipeline/lib/traits.ts` can import the
// vocabulary it validates against. That import runs under `tsx`, where
// `import.meta.env` does not exist (see the note in `core/media/pack.ts`), so
// nothing in this file may touch it.
//
// The words — every label, question and definition — are in
// `src/strings/en/traits.json`; this file holds the vocabulary they describe.

import { hasMessage, t } from '../strings';
import type { Species, SpeciesTraits, TraitKey } from '../types';

/** The five axes, in the order they are asked and shown. */
export const TRAIT_KEYS: readonly TraitKey[] = [
  'size',
  'prey',
  'habitat',
  'feet',
  'nest',
] as const;

/** Something per axis, e.g. its label from `traits.axes.<axis>.label`. */
function perAxis<T>(make: (key: TraitKey) => T): Record<TraitKey, T> {
  return Object.fromEntries(TRAIT_KEYS.map((key) => [key, make(key)])) as Record<TraitKey, T>;
}

/**
 * One message per value, from `traits.values.<axis>.<value>`.
 *
 * A value with no entry is left out rather than filled with its key, so the
 * test that every value has a label and a definition still has something to
 * catch.
 */
function perValue(field: 'label' | 'about'): Record<TraitKey, Record<string, string>> {
  return perAxis((key) =>
    Object.fromEntries(
      TRAIT_VALUES[key].flatMap((value) => {
        const id = `traits.values.${key}.${value}.${field}`;
        return hasMessage(id) ? [[value, t(id)]] : [];
      }),
    ),
  );
}

export const TRAIT_LABEL = perAxis((key) => t(`traits.axes.${key}.label`));

/**
 * What a slot is asking for, worded as the question it is.
 *
 * "Name something it eats" rather than "Prey", because one value solves the slot
 * and the prompt is the only place that can say so.
 */
export const TRAIT_PROMPT = perAxis((key) => t(`traits.axes.${key}.question`));

/**
 * The allowed values, per axis, in display order.
 *
 * `size` is ordinal and ordered by length; the other four are alphabetical,
 * which is the only order that makes a thirteen-chip grid scannable — a
 * "logical" grouping of habitats is one person's logic and everyone else's
 * hunt.
 */
export const TRAIT_VALUES: Record<TraitKey, readonly string[]> = {
  size: ['8-13', '14-20', '21-42', '43-100', '100+'],
  // One invertebrate, not an insect and an invertebrate. They were both here at
  // first and the pair was incoherent — an insect IS an invertebrate, so the
  // vocabulary was asking a learner to know which of two overlapping words was
  // meant. Wingspan's food types make the same call: one bug, covering the
  // caterpillar, the earthworm, the snail and the crab alike, and the split
  // between a flycatcher's diet and a turnstone's is left to the habitat and the
  // bill, where a learner can actually see it.
  prey: ['fish', 'fruit', 'invertebrate', 'meat', 'plant', 'seed'],
  habitat: [
    'aerial',
    'agriculture',
    'arid',
    'coastal',
    'forest',
    'freshwater',
    'grassland',
    'marine',
    'mountain',
    'scrubland',
    'tundra',
    'urban',
    'wetland',
  ],
  feet: ['hunting', 'perching', 'swimming', 'walking'],
  nest: [
    'burrow',
    'cavity',
    'cup',
    'dome',
    'mound',
    'parasitic',
    'pendant',
    'platform',
    'scrape',
  ],
};

/**
 * How a value reads on screen — lowercase, and plural where a bird holding it
 * holds more than one.
 *
 * Per axis rather than one flat map: nothing collides today, but a word that
 * turned up on two axes would silently share a label, and a vocabulary that
 * grows is exactly when that bites.
 */
export const TRAIT_VALUE_LABEL = perValue('label');

/**
 * What an axis means and how it is judged, for the traits guide (`#/traits`).
 *
 * Written against the store as it was actually filled in, not as the words
 * might be read cold — "habitat" includes where a migrant breeds, because the
 * tundra birds were written up that way, and a guide that said otherwise would
 * be teaching a learner to rule the right answer out.
 */
export const TRAIT_ABOUT = perAxis((key) => t(`traits.axes.${key}.about`));

/**
 * What each value means, in a sentence, for the traits guide.
 *
 * Kept beside `TRAIT_VALUE_LABEL` and checked against `TRAIT_VALUES` by
 * `test/traits.test.ts`, so a value added to the vocabulary cannot ship without
 * a line saying what it is.
 */
export const TRAIT_VALUE_ABOUT = perValue('about');

/** A value's display form, falling back to the value for anything unknown. */
export function traitValueLabel(key: TraitKey, value: string): string {
  return TRAIT_VALUE_LABEL[key]?.[value] ?? value;
}

/** Whether a string is in an axis' vocabulary. The pipeline's audit is this. */
export function isTraitValue(key: TraitKey, value: string): boolean {
  return TRAIT_VALUES[key].includes(value);
}

/**
 * Whether something off a manifest or the store is a usable trait set: all five
 * axes present, each a non-empty array, every value in its own vocabulary.
 *
 * Complete or nothing, deliberately. A partial set would have to be handled axis
 * by axis at every reader, and the one reader that matters — the Birdle board —
 * cannot ask a bird a question the pack has no answer to.
 */
export function traitsComplete(value: unknown): value is SpeciesTraits {
  if (!value || typeof value !== 'object') return false;
  const t = value as Record<string, unknown>;
  return TRAIT_KEYS.every((key) => {
    const list = t[key];
    return (
      Array.isArray(list) &&
      list.length > 0 &&
      list.every((v) => typeof v === 'string' && isTraitValue(key, v))
    );
  });
}

/** Whether this species can be asked about — i.e. carries a complete set. */
export function hasTraits(sp: Species | undefined): boolean {
  return traitsComplete(sp?.traits);
}

/**
 * A species' traits as five lists, empty where the pack has nothing.
 *
 * Always safe to read. `traits` is optional on `Species` for the same reason
 * `etym` and the legacy `confusable` shape are: the service worker precaches
 * `region.json` with the app shell, so a client can genuinely be running this
 * code against a manifest written before the field existed. Readers that draw
 * something per axis want empty lists rather than a guard apiece.
 */
export function traitsOf(sp: Species | undefined): SpeciesTraits {
  const t = sp?.traits;
  const out = {} as SpeciesTraits;
  for (const key of TRAIT_KEYS) {
    const list = t?.[key];
    out[key] = Array.isArray(list) ? list.filter((v) => isTraitValue(key, v)) : [];
  }
  return out;
}
